export type SupportAgentSource = {
  uri: string;
};

const KNOWLEDGE_HOST = 'gpanel.io';
const KNOWLEDGE_PATH_PREFIX = '/knowledge/';
const MAX_SOURCE_LINKS = 3;

/** Returns a canonical knowledge URI, or null if it is not allowlisted. Drops query and `#:~:text=` fragments. */
export function allowlistedKnowledgeUri(raw: string): string | null {
  try {
    const url = new URL(raw.trim());
    if (url.username || url.password) {
      return null;
    }
    if (url.protocol !== 'https:') {
      return null;
    }
    if (url.hostname.toLowerCase() !== KNOWLEDGE_HOST) {
      return null;
    }
    if (url.port && url.port !== '443') {
      return null;
    }
    const path = url.pathname;
    if (
      !path.startsWith(KNOWLEDGE_PATH_PREFIX) ||
      path === KNOWLEDGE_PATH_PREFIX
    ) {
      return null;
    }
    if (path.includes('..')) {
      return null;
    }
    return `https://${KNOWLEDGE_HOST}${path}${safeHash(url.hash)}`;
  } catch {
    return null;
  }
}

function safeHash(hash: string): string {
  if (!hash || hash.includes(':~:')) {
    return '';
  }
  return hash;
}

/** Path after `/knowledge/`, Pascal-cased hyphen words, no leading `/`. */
export function knowledgePathDisplay(uri: string): string {
  try {
    const url = new URL(uri);
    let path = url.pathname;
    if (path.startsWith(KNOWLEDGE_PATH_PREFIX)) {
      path = path.slice(KNOWLEDGE_PATH_PREFIX.length);
    }
    return `${pascalCasePathLabel(path)}${knowledgeHashDisplay(url.hash)}`;
  } catch {
    return uri;
  }
}

/** Last path segment, Pascal-cased, plus a real `#Section`. */
export function knowledgeSlugDisplay(uri: string): string {
  try {
    const url = new URL(uri);
    const segments = url.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    if (!last) {
      return knowledgePathDisplay(uri);
    }
    return `${pascalCaseHyphenWords(last)}${knowledgeHashDisplay(url.hash)}`;
  } catch {
    return uri;
  }
}

function knowledgeHashDisplay(hash: string): string {
  if (!hash || hash.includes(':~:')) {
    return '';
  }
  return `#${pascalCaseHyphenWords(hash.slice(1))}`;
}

function pascalCasePathLabel(path: string): string {
  return path.split('/').filter(Boolean).map(pascalCaseHyphenWords).join('/');
}

function pascalCaseHyphenWords(value: string): string {
  return value
    .split('-')
    .map((word) =>
      word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word
    )
    .join('-');
}

export function allowlistedKnowledgeSources(
  sources: unknown
): SupportAgentSource[] {
  if (!Array.isArray(sources)) {
    return [];
  }

  const seen = new Set<string>();
  const allowed: SupportAgentSource[] = [];

  for (const item of sources) {
    const raw =
      typeof item === 'string'
        ? item
        : item && typeof item === 'object' && 'uri' in item
        ? (item as { uri: unknown }).uri
        : undefined;
    if (typeof raw !== 'string') {
      continue;
    }
    const uri = allowlistedKnowledgeUri(raw);
    if (!uri || seen.has(uri)) {
      continue;
    }
    seen.add(uri);
    allowed.push({ uri });
    if (allowed.length === MAX_SOURCE_LINKS) {
      break;
    }
  }

  return allowed;
}
